import type { DoctenaConnectionMode } from '../enums.js';
import { DOCTENA_SYNC_INTERVAL_MINUTES } from './doctena.js';

/**
 * Console de plateforme — les synchronisations Doctena de tous les cabinets.
 *
 * Une ligne par cabinet dont la synchro est **active**, sa date de dernier import et un statut.
 * **Ni identifiant Doctena, ni clé, ni email du compte, ni cabinet Doctena choisi**, et aucune
 * donnée de patient : des compteurs, des horodatages et des codes. La cause d'une panne est un code
 * à nous, jamais le texte renvoyé par le fournisseur.
 */

/**
 * Au-delà de ce délai sans import réussi, une synchro active est « en retard ».
 *
 * La tâche `sync-doctena` se réveille toutes les deux minutes et relance chaque connexion
 * `DOCTENA_SYNC_INTERVAL_MINUTES` après sa dernière réussite : en régime normal, deux imports
 * sont espacés de sept minutes au plus. Le disjoncteur de quota coupe trente minutes au moins ;
 * douze cadences laissent passer une pause de quota sans alerter, et pas une panne durable.
 */
export const DOCTENA_SYNC_STALE_AFTER_MINUTES = 12 * DOCTENA_SYNC_INTERVAL_MINUTES;

/** Code rangé quand une synchro échoue sur autre chose qu'une erreur métier nommée. */
export const DOCTENA_UNEXPECTED_ERROR_CODE = 'unexpected';

/** `late` et `error` sont les deux façons de « ne plus marcher » ; l'écran les compte ensemble. */
export const doctenaSyncHealthStatuses = ['ok', 'late', 'error'] as const;
export type DoctenaSyncHealthStatus = (typeof doctenaSyncHealthStatuses)[number];

/**
 * Pourquoi une synchro ne marche plus, en liste fermée et traduite à l'écran.
 * - `never_imported` : activée depuis plus que le seuil, sans un seul import réussi ;
 * - `stale` : le dernier import réussi est plus vieux que le seuil ;
 * - `auth_failed` : Doctena refuse les identifiants — cela ne se répare pas tout seul ;
 * - `provider_unavailable`, `rate_limited` : Doctena en panne, ou quota dépassé, à répétition ;
 * - `sync_failed` : toute autre erreur répétée de notre côté.
 */
export const doctenaSyncCauses = [
  'never_imported',
  'stale',
  'auth_failed',
  'provider_unavailable',
  'rate_limited',
  'sync_failed',
] as const;
export type DoctenaSyncCause = (typeof doctenaSyncCauses)[number];

export interface PlatformDoctenaSyncDto {
  organizationId: string;
  organizationName: string;
  /** Mode effectif : un compte pour le cabinet, ou un par praticien. */
  mode: DoctenaConnectionMode;
  /** Connexions actives du cabinet : une en mode cabinet, une par praticien relié sinon. */
  connections: number;
  /** Calendriers Doctena associés à un praticien, toutes connexions confondues. */
  calendars: number;
  /** Dernier import réussi des rendez-vous ; le plus ancien quand il y a plusieurs connexions. */
  lastImportAt: string | null;
  status: DoctenaSyncHealthStatus;
  /** Nul quand tout va bien. */
  cause: DoctenaSyncCause | null;
  /** Code de l'échec en cours (`doctena_unauthorized`…), le nôtre, pour le diagnostic. */
  errorCode: string | null;
  lastErrorAt: string | null;
}

export interface PlatformDoctenaSyncsDto {
  /** L'instant de la lecture : la référence des durées affichées. */
  checkedAt: string;
  syncIntervalMinutes: number;
  staleAfterMinutes: number;
  /** Synchros en retard ou en erreur : le chiffre de l'alerte. */
  broken: number;
  /** Les pannes d'abord, puis par nom de cabinet. */
  syncs: PlatformDoctenaSyncDto[];
}
